<div class="template-course-list__header">
    <Select
        id="template-select"
        value={selectedTemplateType.value}
        on:change={e => selectedTemplateTypeChanged(e.detail)}
        label={formatLanguageString(i18n.xColon, i18n.courseType)}
        labelPosition="left"
    >
        {#each availableTemplateTypes as type}
            <option value={type.value}>
                {type.text}
            </option>
        {/each}
    </Select>
    <SearchInput
        isFocused={true}
        value={searchString}
        placeholder={i18n.searchForCourses}
        label={i18n.searchForCourses}
        clearText={i18n.clearSearch}
        describedBy="search-result-text"
        on:keyup={event => {
            searchCoursesHandler(getSearchValueFromEvent(event));
        }}
        on:clear={event => {
            searchCoursesHandler(getSearchValueFromEvent(event));
        }}
    />
</div>
<div class="template-course-list__container">
    {#await courses}
        <LoadingState />
    {:then courses}
        {#if courses && courses.length > 0}
            <span id="search-result-text" class="screen-reader">{getSearchResultsText(courses)}</span>
            <List>
                <thead slot="header">
                    <ListElement header>
                        <Cell header />
                        <Cell header>
                            <span slot="header">{i18n.courses}</span>
                        </Cell>
                    </ListElement>
                </thead>
                <tbody slot="body">
                    {#each courses as course}
                        <ListElement
                            on:click={() => {
                                onTemplateSelected(course);
                            }}
                        >
                            <Cell type="course-icon">
                                <span class={course.courseIcon} slot="data"></span>
                            </Cell>
                            <Cell type="course-info">
                                <div slot="data">
                                    <div>
                                        <div>
                                            <span class="course-title"
                                                >{getCourseTitle(course)}</span
                                            >
                                        </div>
                                        <div class="course-additional-information">
                                            {#if (selectedTemplateType.value === readyMadeContentTemplateTypes.yourCourses
                                            || selectedTemplateType.value === readyMadeContentTemplateTypes.courseTemplates)
                                            && course.code}
                                                <span
                                                    >{formatLanguageString(
                                                        i18n.xColonY,
                                                        i18n.code,
                                                        course.code
                                                    )}</span
                                                >
                                            {/if}

                                            <span
                                                >{formatLanguageString(
                                                    i18n.xColonY,
                                                    i18n.lastUpdated,
                                                    course.lastUpdated
                                                )}</span
                                            >

                                            {#if selectedTemplateType.value === readyMadeContentTemplateTypes.fromPublishers
                                            || selectedTemplateType.value === readyMadeContentTemplateTypes.otherSites}
                                                <span
                                                    >{formatLanguageString(
                                                        i18n.xColonY,
                                                        i18n.belongsTo,
                                                        course.belongsTo
                                                    )}</span
                                                >
                                            {/if}
                                        </div>
                                    </div>
                                    <IconButton
                                        htmlType="button"
                                        iconOnly={true}
                                        iconClass="explore-button"
                                        type="plain"
                                        ariaLabel="{getCourseTemplateButtonText(course)}"
                                        text="{getCourseTemplateButtonText(course)}"
                                    />
                                </div>
                            </Cell>
                        </ListElement>
                    {/each}
                </tbody>
            </List>
        {:else}
            <EmptyState iconName="courses" title={emptyStateMessage} showButton={false} />
            <span id="search-result-text" class="screen-reader">{emptyStateMessage}</span>
        {/if}
    {:catch error}
        <p>{error.message}</p>
    {/await}
</div>

<script>
    import List from "@itslearning/prometheus/assets/lists/List/v1/List.svelte";
    import ListElement from "@itslearning/prometheus/assets/lists/ListElement/v1/ListElement.svelte";
    import Select from "@itslearning/prometheus/assets/inputs/Select/v1/Select.svelte";
    import Cell from "@itslearning/prometheus/assets/lists/Cell/v1/Cell.svelte";
    import EmptyState from "@itslearning/prometheus/assets/nodes/EmptyState/v1/EmptyState.svelte";
    import SearchInput from "@itslearning/prometheus/assets/inputs/SearchInput/v1/SearchInput.svelte";
    import IconButton from "@itslearning/prometheus/assets/inputs/IconButton/v1/IconButton.svelte";
    import LoadingState from "@its-ui/common-components/LoadingState/LoadingState.svelte";

    import { formatLanguageString } from "@itslearning/atlas/strings/format.js";
    import { createEventDispatcher } from "svelte";
    import { readyMadeContentTemplateTypes } from "../../../constants";
    import { debounce } from "@itslearning/atlas/dom/Ratecontrol";

    export let availabilityOfTemplateTypes;
    export let selectedTemplateType;
    export let i18n = {};
    export let isSearch;
    export let courses;
    let searchString = "";

    const dispatch = createEventDispatcher();
    const getSearchValueFromEvent = event => (event.currentTarget ? event.currentTarget.value : "");
    const searchCoursesHandler = debounce(
        async searchValue => await searchForCourseTemplates(searchValue),
        1000
    );

    $: availableTemplateTypes = getAvailableTemplateTypes(availabilityOfTemplateTypes);

    $: emptyStateMessage = isSearch ? i18n.searchNoResultsFound : i18n.noCourses;

    $: if (!selectedTemplateType && availableTemplateTypes && availableTemplateTypes.length > 0) {
        selectedTemplateType = availableTemplateTypes[0];
    }

    function getCourseTemplateButtonText(course) {
        return formatLanguageString(i18n.planner2_TableView_Topics_Select_X, getCourseTitle(course));
    }

    function getSearchResultsText(courses) {
        return courses.length > 1
            ? formatLanguageString(i18n.resultsCount, courses.length)
            : formatLanguageString(i18n.resultCount, courses.length);
    }
    function getAvailableTemplateTypes(availabilityOfTemplateTypes) {
        const templateTypes = [];

        if (availabilityOfTemplateTypes.showYourCourses) {
            templateTypes.push({
                text: i18n.yourCourses,
                value: readyMadeContentTemplateTypes.yourCourses
            });
        }

        if (availabilityOfTemplateTypes.showCourseTemplates) {
            templateTypes.push({
                text: i18n.courseTemplates,
                value: readyMadeContentTemplateTypes.courseTemplates
            });
        }

        if (availabilityOfTemplateTypes.showFromPublishers) {
            templateTypes.push({
                text: i18n.fromPublishers,
                value: readyMadeContentTemplateTypes.fromPublishers
            });
        }

        if (availabilityOfTemplateTypes.showOtherSites) {
            templateTypes.push({
                text: i18n.otherSites,
                value: readyMadeContentTemplateTypes.otherSites
            });
        }

        return templateTypes;
    }

    export function setDefaultSelectedTemplateType() {
        selectedTemplateType = availableTemplateTypes && availableTemplateTypes.length > 0
        ? availableTemplateTypes[0]
        : null;
    }

    function getCourseTitle(course) {
        return course.title && course.friendlyName
            ? formatLanguageString(i18n.courseTitleFriendlyName, course.title, course.friendlyName)
            : course.title;
    }

    function selectedTemplateTypeChanged(templateType) {
        dispatch(
            "selectedTemplateTypeChanged",
            availableTemplateTypes.find(x => x.value === templateType)
        );
    }

    function searchForCourseTemplates(newSearchString) {
        if (newSearchString === searchString) {
            return;
        }

        searchString = newSearchString;

        dispatch("searchForCourseTemplates", searchString);
    }

    function onTemplateSelected(course) {
        dispatch("templateSelected", {
            templateId: course.courseId,
            type: selectedTemplateType.value,
            name: course.title,
            avatar: course.courseIcon,
            organisationId: course.organisationId,
            hasPlans: course.hasPlans,
            hasTerms: course.hasTerms
        });
    }
</script>
